import { jsx as _jsx } from "react/jsx-runtime";
// =============================================================================
//  Copyright   : 2018 semantics GmbH
//  Date        : $Date: 2026-02-20 12:58:13 +0100 (Fri, 20 Feb 2026) $
//  Author      : Dirk Rothe
//  Worker      : $Author: d.rothe $
//  Revision    : $Rev: 160911 $
//  Purpose     : uix core selectors
//  Redux state can be thought of like a database and selectors like
//  reusable SELECT queries to get useful data from the database.
// =============================================================================
// ---Third-party imports
import React from 'react';
import { createSelector } from 'reselect';
// ---Framework imports
import { get, dict, mapItems, filterItems, values, flatten, has, size, mergeDeep } from 'ldash';
import log from 'uix/logging';
import { translate } from 'uix/i18n';
import { toQueryString } from 'uix/utils/url';
// ---Globals
let EMPTY = {};
let EMPTY_LIST = [];
// get session id
export let getSessionId = (root)=>get(root, 'meta.user.session');
let _apiurlSelector = (root)=>root.config.deploy.mode == 'vls' ? '' : root.config.apiurl;
let build_url = (apiurl, path, params)=>{
    params = dict(filterItems(params, (k, v)=>v !== undefined));
    return `${apiurl}${path || ''}${size(params) > 0 ? '?' + toQueryString(params) : ''}`;
};
const apiurlSelector = createSelector([
    _apiurlSelector,
    (root)=>root.config.instance.baseUrl,
    (root)=>root.config.domains
], (apiurl, baseUrl, domains)=>({
        toString: ()=>apiurl,
        // empty api url in deploy mode vls (relative path in prod)
        // points to remote FQDN in dev-mode (webpack dev server)
        href: (path, params)=>build_url(apiurl, path, params),
        // also prepend domain prefix to path
        domain_href: (odid_or_dname, path, params)=>{
            let dname = domains.name2prefix[odid_or_dname] ? odid_or_dname : domains.odid2name[odid_or_dname];
            if (domains.name2prefix[dname]) {
                path = `/${domains.name2prefix[dname]}${path.startsWith('/') ? '' : '/'}${path}`;
            }
            return build_url(apiurl, path, params);
        },
        // using always the server-send PROTO+FQDN
        // do not use this for navigation only for display/export
        baseUrl: (path_query = '')=>`${baseUrl}${path_query}`
    }));
// centralize custom url building
const urlsSelector = createSelector([
    apiurlSelector
], (apiurl)=>({
        webcache: ({ otid, width, query })=>apiurl.href(`/download/webcache/${width}/${otid}`, query),
        pageBoxCache: ({ rel_otid, page_otid, width = 304 })=>apiurl.href(`/image/serveTitlePageBoxCache/${rel_otid}/${page_otid}/${width}`)
    }));
const selectInstanceConfig = (root)=>root.config.instance;
const selectInstanceTypes = (root)=>root.config.instance.itypes;
// get current request state
export let requestSelector = (root)=>root.meta.request;
export let selectRootDomain = (root)=>root.config.domains.rootDomain;
// get templating class cfg for current view context
export let selectRequestClassCfg = (root)=>{
    let { dname, classname } = root.meta.request;
    if (has(root.config.ddata, dname)) {
        return root.config.templating[dname].classes[classname];
    }
    return null;
};
// get all templating classes cfg for current domain context
export let selectDomainClassCfgs = (root)=>{
    let { dname } = root.meta.request;
    return get(root, `config.templating[${dname}].classes`, EMPTY);
};
const getDomainDataAttribute = (attr_name)=>(root)=>{
        let { dname } = root.meta.request;
        return get(root.config.ddata, dname, {})[attr_name] || null;
    };
export let selectRequestTypes = getDomainDataAttribute('types');
let _selectRequestI18N = createSelector([
    (root)=>root.meta.request.dname,
    (root)=>root.meta.language,
    (root)=>root.config.ddata
], (dname, language, ddata)=>{
    if (!dname || !has(ddata, dname)) {
        return EMPTY;
    }
    return (ddata[dname].i18nResolved || EMPTY)[language] || EMPTY;
});
let selectAllDomainClassCfgs = createSelector([
    (root)=>root.config.domains,
    (root)=>root.config.templating
], (domains, templating)=>{
    return dict(domains.names.map((dname)=>[
            dname,
            get(templating, `${dname}.classes`, null)
        ]));
});
export let selectRequestI18N = createSelector([
    _selectRequestI18N,
    (root)=>root.meta.language,
    selectInstanceTypes
], (i18n, language, itypes)=>({
        // merge all keys into dict
        ...i18n,
        _language: language,
        // raw string output
        get: (...args)=>translate(i18n, ...args),
        // build a span element that is more debuggable
        span: (identifier, ...args)=>/*#__PURE__*/ _jsx("span", {
                className: "vl_i18n",
                children: translate(i18n, identifier, ...args)
            }),
        getType: ((itypes)=>{
            let i18n_keys = dict(flatten(mapItems(itypes.byName, (name, [dt_int, st_int])=>{
                name = name.replace(/ /g, '_');
                let dt_name = itypes.datatypeMap[dt_int];
                let text = `types_${dt_name}_${name}`;
                return [
                    [
                        `${dt_int}_${st_int}`,
                        text
                    ],
                    [
                        `${dt_name}_${st_int}`,
                        text
                    ],
                    [
                        name,
                        text
                    ]
                ];
            })));
            return (typeName_or_dt_int, st_int)=>{
                if (!i18n) {
                    return 'unloaded';
                }
                if (st_int === undefined) {
                    // typeName case
                    return i18n[i18n_keys[typeName_or_dt_int]] || typeName_or_dt_int;
                } else {
                    let k = `${typeName_or_dt_int}_${st_int}`;
                    return i18n[i18n_keys[k]] || k;
                }
            };
        })(itypes)
    }));
export let selectRequestDomain = createSelector([
    (root)=>root.meta.request.dname,
    (root)=>root.config.domains
], (dname, domains)=>domains.data[dname]);
export let selectRequestStates = (root)=>root.config.states[root.meta.request.dname];
// get domain config for current view context
const _selectRequestDomainCfg = (root)=>{
    let { dname } = root.meta.request;
    if (has(root.config.ddata, dname)) {
        return root.config.ddata[dname].dconfig;
    }
    return null;
};
export const selectRequestDomainCfg = createSelector([
    _selectRequestDomainCfg,
    (root)=>root.debug.mergeHotFS,
    (root)=>root.debug.dconfigOverride
], (dconfig, mergeHotFS, dconfigOverride)=>mergeHotFS && dconfigOverride ? mergeDeep(dconfig, dconfigOverride, true) : dconfig);
// get all templating classes cfg for current domain context
export let selectDomainNamedParts = (root)=>{
    let { dname } = root.meta.request;
    return get(root, `config.templating[${dname}].parts`, EMPTY);
};
export const selectAllDomains = (root)=>root.config.domains;
const selectRequest = (root)=>root.meta.request;
export const selectRequestSubdomains = createSelector([
    selectRequest,
    selectAllDomains
], (request, domains)=>getSubdomains(request.dname, domains));
export const getSubdomains = (dname, domains)=>{
    /* select subtree of domains as list (depth first), auto include self
   */ let includeSelf = true;
    //~ let {domains} = root.config
    let data = values(domains.data);
    let recurse = (name)=>{
        let children = data.filter((d)=>d.parent == name);
        children = [
            ...children,
            ...flatten(children.map((d)=>recurse(d.name)))
        ];
        return children;
    };
    let result = recurse(dname);
    if (includeSelf) {
        result = [
            domains.data[dname],
            ...result
        ];
    }
    return result;
};
export const getAncestorDomains = (dname, domains, inclusive = false)=>{
    /* return list of ancestor domains
   */ let ancestors = [];
    let current = domains.data[dname];
    if (!current) {
        log.warn(`${dname} not loaded`);
        return ancestors;
    }
    if (inclusive) {
        ancestors.push(current);
    }
    while(current.parent){
        current = domains.data[current.parent];
        ancestors.push(current);
    }
    return ancestors;
};
let selectRequestAncestorAndSelfDomains = (includeSelf)=>createSelector([
        selectRequest,
        selectAllDomains
    ], (request, domains)=>getAncestorDomains(request.dname, domains, includeSelf));
export let selectAncestorDomains = (dname)=>(root)=>{
        /* select ancestors starting from dname as list
   */ let { domains } = root.config;
        return getAncestorDomains(dname, domains);
    };
export let selectId = (root)=>root.meta.request.id;
export let getLanguage = (root)=>root.meta.language;
// monotonic increasing counter on each go() action, kind of a view id
export let getRunId = (root)=>get(root, 'data.runId', 0);
export let selectInitCacheKeys = (root)=>{
    let { domain } = root.config.deploy.domain;
    let instanceCache = root.config.instance.cacheKeys;
    if (domain) {
        let ddata = root.config.ddataCache[domain];
        if (ddata) {
            return {
                [domain]: ddata.cacheKeys,
                __instance__: instanceCache
            };
        } else {
            return {
                __instance__: instanceCache
            };
        }
    } else {
        let domainKeys = dict(mapItems(root.config.ddataCache, (dname, v)=>[
                dname,
                v.cacheKeys
            ], (dname, v)=>!!v && !!v.cacheKeys));
        return {
            ...domainKeys,
            __instance__: instanceCache
        };
    }
};
export const isDebugOn = (root)=>get(root, 'meta.user.groups', []).includes('debug') && root.debug.on !== false;
const selectUser = createSelector([
    (root)=>root.meta.user,
    selectRequestDomain
], (user, domain)=>{
    let req_dname = domain.name;
    let hasGroup = (grp, dname = req_dname)=>{
        /* check if current user is in group
      use request domain if no dname is given
      */ return user && user.groups.includes(grp) || dname && user.localgroups && user.localgroups[dname]?.includes(grp);
    };
    return {
        ...user,
        hasGroup
    };
});
// needs unbound filter (on tab-level, not on part-level)
const ancestorAxis = (root)=>get(root.data.parts, 'Unbound.ancestorAxisJson.data.ancestorAxis', EMPTY_LIST);
export const buildAncestorAxis = (aaxis, reqTypes, i18n, itypes, domains)=>{
    let currentRecord = aaxis[0];
    if (!currentRecord) {
        return {
            ancestors: [],
            ancestorsAndSelf: []
        };
    }
    let enrich = (record)=>{
        let tcfg = reqTypes.byId[record.datatype]?.[record.subtype];
        if (!tcfg) {
            let fallback_name = values(itypes.byId[record.datatype])[0];
            tcfg = {
                name: fallback_name,
                datatypeName: itypes.datatypeMap[record.datatype]
            };
            log.warn(`${record.datatype}/${record.subtype} not active, fallback to pseudo subtype ${tcfg.name}`);
        }
        let dname = domains.odid2name[record.odid];
        let translation = i18n?.[`types_${tcfg.datatypeName}_${tcfg.name}`];
        return {
            ...record,
            datatypeName: tcfg.datatypeName,
            dname,
            type: tcfg.name,
            translation,
            label: record.caption || translation,
            tcfg
        };
    };
    // XXX: maybe add a parent() func that gives the same stuff for ancestors
    let ancestors = aaxis.slice(1).map((r)=>enrich(r));
    currentRecord = enrich(currentRecord);
    return {
        ...currentRecord,
        ancestors,
        ancestorsAndSelf: [
            currentRecord,
            ...ancestors
        ]
    };
};
const currentNode = createSelector([
    ancestorAxis,
    selectRequestTypes,
    _selectRequestI18N,
    selectInstanceTypes,
    selectAllDomains
], buildAncestorAxis);
const currentNodePhysical = (root)=>{
    const node = currentNode(root);
    return node.otlink ? {
        ...node,
        otid: node.otlink,
        odid: node.odlink
    } : node;
};
const selectDomainDescription = (root)=>{
    const { config, meta } = root;
    const { language, request } = meta;
    const { dname } = request;
    const { data } = config.domains;
    if (has(data, dname)) {
        const domainConfig = data[dname];
        if (has(domainConfig.lang2description, language)) {
            return domainConfig.lang2description[language];
        }
        return domainConfig.description || domainConfig.name;
    }
    return null;
};
// this is the prefered access form: import {Sel} from 'uix/registries'
export const Select = {
    // instance wide
    iconfig: selectInstanceConfig,
    itypes: selectInstanceTypes,
    domains: selectAllDomains,
    // request local / domain local
    request: requestSelector,
    dconfig: selectRequestDomainCfg,
    types: selectRequestTypes,
    activeDescendantTypes: getDomainDataAttribute('activeDescendantTypes'),
    states: selectRequestStates,
    i18n: selectRequestI18N,
    domain: selectRequestDomain,
    subdomains: selectRequestSubdomains,
    // data from config.domains starting from leaf to root
    ancestorAndSelfDomains: selectRequestAncestorAndSelfDomains(true),
    ancestorDomains: selectRequestAncestorAndSelfDomains(false),
    classCfg: selectRequestClassCfg,
    domainClassCfgs: selectDomainClassCfgs,
    allDomainClassCfgs: selectAllDomainClassCfgs,
    domainDescription: selectDomainDescription,
    // id local - depend on available ancestor axis (filter)
    ancestorAxis,
    currentNode,
    currentNodePhysical,
    // user local
    user: selectUser,
    language: (root)=>root.meta.language,
    sessionId: getSessionId,
    // utility
    apiurl: apiurlSelector,
    // build custom urls (webcache, ...)
    urls: urlsSelector,
    runId: getRunId,
    appstate: (root)=>root.meta.state,
    isOffline: (root)=>root.meta.state == 'API_OFFLINE',
    // debug
    isDebugOn,
    hmrGen: (root)=>root.debug.hmr?.gen
};
